import React, { useMemo, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_LANGUAGES_ALL } from "graphql/query/language-gql";
import { GET_TRANSLATION_AI_REQUESTS } from "graphql/query/translation-ai-gql";
import Language from "components/language";
import Settings from "components/settings";
import { PageWrapDefault, Tables } from "components/layout";
import { Localize } from "components/service";
import { useActiveMenuItem, useBreadCrumbs, useDebounce } from "components/use-hooks";

import './settings.scss';


const routeUrl = Settings.Const.translationAiRequestsPath;
const tableHelper = Settings.Helpers.TranslationAiTable.Requests;
const translationAiTableHelper = Settings.Helpers.TranslationAiTable;
const analyticsRowsLimit = 500;

const TranslationAiRequestsPage = () => {

    useActiveMenuItem([ "settings-translation-ai-requests" ], [ "settings" ]);

    const breadcrumbs = [
        {
            label: Localize({ children: "BREADCRUMBS.Label_Settings" }).props.children,
            path: `${ Settings.Const.basePath }`
        },
        {
            label: Localize({ children: "BREADCRUMBS.Label_TranslationAiRequests" }).props.children,
            path: `${ routeUrl }`
        }
    ];

    useBreadCrumbs(breadcrumbs);

    let match = useRouteMatch(`${ routeUrl }/page/:pageNum`);

    const [ searchText, setSearchText ] = useState("");
    const [ filters, setFilters ] = useState();
    const debouncedSearchText = useDebounce(searchText);
    const entityWhere = useMemo(() => (
        translationAiTableHelper.withEntityRequestFilter(filters)
    ), [ filters ]);

    const { data: analyticsData } = useQuery(GET_TRANSLATION_AI_REQUESTS, {
        variables: {
            text: debouncedSearchText,
            first: analyticsRowsLimit,
            page: 1,
            where: entityWhere,
            orderBy: [ { column: "CREATED_AT", order: "DESC" } ],
        },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first'
    });

    const analyticsRecords = analyticsData?.translateAiRequests?.data || [];

    const { data: languagesData } = useQuery(GET_LANGUAGES_ALL, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first'
    });

    const languageMap = useMemo(() => (
        Language.Helpers.Options.toMap(languagesData?.languagesAll || [])
    ), [ languagesData ]);

    return (
        <PageWrapDefault
            className="page-settings page-translation-ai-requests"
            dataExist={ true }
            title={ <Localize>PAGES.Title_TranslationAiRequests</Localize> }
        >
            <div className="translation-ai-analytics-layout">
                <div className="translation-ai-table-panel">
                    <div className="translation-ai-request-overview">
                        <div className="translation-ai-request-note">
                            <strong><Localize>TABLES.Title_RequestBatchLog</Localize></strong>
                            <span><Localize>TABLES.Description_RequestBatchLog</Localize></span>
                        </div>
                    </div>

                    <Tables.Main
                        model="translateAiRequests"
                        query={ GET_TRANSLATION_AI_REQUESTS }
                        varSet={ { perPage: 25 } }
                        routeUrl={ `${ routeUrl }` }
                        currentPage={ match ? parseInt(match.params.pageNum) : 1 }
                        searchText={ searchText }
                        objFilter={ entityWhere }
                        objOrderBy={ [ { column: "CREATED_AT", order: "DESC" } ] }
                        tableHelper={ tableHelper }
                        tableLayout="auto"
                        extraClass="translation-ai-compact-table"
                        currentObj={ { languageMap } }
                    >
                        <div className="table-action-bar">
                            <div className="col">
                                <Settings.Filters.TranslationAiTable
                                    setSearchText={ setSearchText }
                                    setFilters={ setFilters }
                                    dateColumn="CREATED_AT"
                                    searchPlaceholder="SEARCH.Input_Placeholder_TranslationAiRequests"
                                    showRequestFilters={ true } />
                            </div>
                        </div>
                    </Tables.Main>
                </div>
                <div className="translation-ai-chart-panel">
                    <Settings.Charts.TranslationAi.Requests records={ analyticsRecords } languageMap={ languageMap } />
                </div>
            </div>
        </PageWrapDefault>
    );
};

export default TranslationAiRequestsPage;
